/**
 * 只处理数据（转类型）、判断是否可以调用API（如 requiresLogin）、在invalid_token时给User Service发送消息
 * 禁止做其他的事情（如保存用户token等）
 *
 * 返回值必须是 Promise<xxxResponse>
 *
 * 所有调用api.xxx()的，都要记得.catch();
 */

import { Injectable } from "@angular/core";
import { env } from "../config/environment";
import { Http, Response } from "@angular/http";
import "rxjs/add/operator/catch";
import "rxjs/add/operator/map";
import "rxjs/add/operator/finally";
import "rxjs/add/operator/toPromise";
import { Util } from "./util";
import {
  LoadingController,
  ToastController,
  AlertController,
} from "ionic-angular";

let resultCache = {};

@Injectable()
export class Api {
  analyticsClientId;
  constructor(
    private http: Http,
    private util: Util,
    private toastCtrl: ToastController,
    public loadingCtrl: LoadingController,
    private alertCtrl: AlertController
  ) {
    this.http = http;
    if (!localStorage["analyticsClientId"]) {
      localStorage["analyticsClientId"] =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
    }

    this.analyticsClientId = localStorage["analyticsClientId"];
  }

  request(
    path: string,
    params,
    {
      requiresLogin = false,
      showLoading = true,
      muteErrorToast = false,
      throwWhenError = false,
    }
  ): Promise<any> {
    const userService = window["UserService"];

    if (requiresLogin && !userService.isLoggedIn()) {
      userService.showLogin();
      return userService.notLoggedInRejection();
    }

    let loading;
    if (showLoading) {
      loading = this.loadingCtrl.create({
        content: "载入中，请稍等",
      });
      loading.present();
    }
    path = path.replace(/\./g, "/");

    if (userService && userService.isLoggedIn()) {
      params["token"] = userService.token;
    }

    return new Promise((resolve, reject) => {
      let completed = false;
      let isPromiseFulfilled = false;
      let alertShown = false;
      let alert = null;
      setInterval(() => {
        if (!completed && !alertShown && !muteErrorToast) {
          alertShown = true;
          alert = this.alertCtrl.create({
            title: "网络慢",
            message: "您希望如何操作?",
            buttons: [
              {
                text: "放弃",
                handler: () => {
                  alertShown = false;
                  if (!completed && !isPromiseFulfilled) {
                    if (showLoading) loading.dismiss();
                    completed = true;
                    isPromiseFulfilled = true;
                    reject({});
                  }
                },
              },
              {
                text: "重试",
                handler: () => {
                  alertShown = false;

                  if (!completed) {
                    if (showLoading) loading.dismiss();
                    completed = true;
                    this.request(path, params, {
                      requiresLogin,
                      showLoading,
                      muteErrorToast,
                      throwWhenError,
                    }).then(
                      (value) => {
                        if (!isPromiseFulfilled) {
                          isPromiseFulfilled = true;
                          resolve(value);
                        }
                      },
                      (reason) => {
                        if (!isPromiseFulfilled) {
                          isPromiseFulfilled = true;
                          reject(reason);
                        }
                      }
                    );
                  }
                },
              },
              {
                text: "等待",
                handler: () => {
                  alertShown = false;
                },
              },
            ],
          });
          alert.present();
        }
      }, 5000);

      let doFinally = function () {
        completed = true;
        if (showLoading) loading.dismiss();
        if (alertShown) {
          alert.dismiss();
        }
      };
      this.http.post(env.server_url + path, params).subscribe(
        (res: Response) => {
          let body = res.json();
          if (!isPromiseFulfilled) {
            isPromiseFulfilled = true;
            resolve(body || {});
          }
          doFinally();
        },
        (error: Response | any) => {
          let reason = error.json() || {};
          if (reason.errMsg == "invalid_token") {
            setTimeout(() => {
              userService.showLogin();
              return userService.notLoggedInRejection();
            }, 200);
          } else if (reason.errMsg == "invalid_data") {
            if (!muteErrorToast)
              this.toastCtrl
                .create({
                  message: "输入无效：" + reason.message,
                  duration: 1500,
                })
                .present();
          } else if (error.status == 0) {
            if (!muteErrorToast)
              this.toastCtrl
                .create({
                  message: "无法连接到服务器，请检查网络后再试。",
                  showCloseButton: true,
                  closeButtonText: "关闭",
                  position: "middle",
                  cssClass: "important-toast",
                })
                .present();
          } else {
            if (!muteErrorToast)
              this.toastCtrl
                .create({
                  message: "出错了: " + (reason.errMsg || reason.message),
                  duration: 1500,
                })
                .present();
          }
          if (!isPromiseFulfilled) {
            isPromiseFulfilled = true;
            if (throwWhenError) {
              reject(reason);
            }
            // reject(reason);
          }
          doFinally();
        }
      );
    });
  }

  getUserInfo(silent = false) {
    return this.request(
      "user.info",
      {},
      {
        requiresLogin: true,
      }
    );
  }

  public createWxLogin(userId, verifyToken) {
    return this.request(
      "user.create-wx-login",
      { verify_token: verifyToken, user_id: userId },
      {
        muteErrorToast: true,
        throwWhenError: true,
        requiresLogin: true,
      }
    );
  }

  public loginByWx(loginToken) {
    return this.request(
      "user.login-by-wx",
      { login_token: loginToken },
      {
        muteErrorToast: true,
        throwWhenError: true,
        requiresLogin: true,
      }
    );
  }

  getItemDetail(id) {
    return this.request("item.detail", { id }, {});
  }

  getDiscountedSoloPrice(itemId) {
    return this.request(
      "item.get-discounted-solo-price",
      { item_id: itemId },
      {
        requiresLogin: true,
        showLoading: false,
      }
    );
  }

  getCampaignsList(showType, showLoading = true) {
    return this.request(
      "campaign.list",
      { show_type: showType },
      { showLoading, requiresLogin: true }
    );
  }

  getCampaignDetail(id, showLoading = true) {
    return this.request(
      "campaign.detail",
      { id },
      { showLoading: showLoading }
    );
  }

  createCampaign({ itemId, follow = null, solo = false }) {
    return this.request(
      "campaign.create",
      { item_id: itemId, follow, solo },
      {
        requiresLogin: true,
      }
    );
  }

  participateInCampaign(campaignId) {
    return this.request(
      "campaign.participate",
      { campaign_id: campaignId },
      {
        requiresLogin: true,
      }
    );
  }

  getOrderDetail(campaignId) {
    return this.request(
      "order.detail",
      { campaign_id: campaignId },
      {
        requiresLogin: true,
      }
    );
  }

  getOrderState(orderId) {
    return this.request(
      "order.state",
      { order_id: orderId },
      {
        showLoading: false,
        muteErrorToast: true,
        requiresLogin: true,
      }
    );
  }

  purchaseCampaign(campaignId) {
    return this.request(
      "order.pay",
      { campaign_id: campaignId },
      {
        requiresLogin: true,
      }
    );
  }

  placeOrder(campaignId) {
    return this.request(
      "order.place-order",
      { campaign_id: campaignId },
      {
        requiresLogin: true,
      }
    );
  }

  weixinPayOrder(orderId) {
    return this.request(
      "order.weixin-pay-order",
      { order_id: orderId },
      {
        requiresLogin: true,
      }
    );
  }

  doDtbPay(data: any) {
    return this.request("order.do-dtb-pay", data, {
      requiresLogin: true,
    });
  }

  mWebPay(data: any) {
    return this.request("order.mweb-pay", data, {
      requiresLogin: false,
    });
  }

  weixinSignParams() {
    return this.request(
      "weixin-sign-params",
      {},
      {
        showLoading: false,
        muteErrorToast: true,
      }
    );
  }

  bindAccount(credentials) {
    return this.request("user.bind-account", credentials, {
      requiresLogin: true,
    });
  }

  requestBindVerificationCode(data) {
    return this.request("user.request-bind-verification-code", data, {
      requiresLogin: true,
    });
  }

  requestLoginVerificationCode(data) {
    return this.request("user.request-login-verification-code", data, {});
  }

  doBindByVerificationCode(data) {
    return this.request("user.do-bind-by-verification-code", data, {
      requiresLogin: true,
    });
  }

  doLoginByVerificationCode(data) {
    return this.request("user.do-login-by-verification-code", data, {});
  }

  myCashReward(id, showLoading = true) {
    return this.request(
      "campaign.cash-reward",
      { id },
      {
        showLoading: showLoading,
        requiresLogin: true,
      }
    );
  }

  getAccountBindInfo() {
    return this.request(
      "user.get-account-bind-info",
      {},
      {
        requiresLogin: true,
      }
    );
  }

  unbindAccount() {
    return this.request(
      "user.unbind-account",
      {},
      {
        requiresLogin: true,
      }
    );
  }

  collect(data) {
    data.$meta = {
      clientId: this.analyticsClientId,
      userId: window["UserService"].id || "",
    };
    return this.request("collect", data, {
      showLoading: false,
      muteErrorToast: true,
    });
  }

  getSharePoster(parsedData: any, showLoading = true, muteErrorToast = false) {
    return this.request("user.get-share-poster", parsedData, {
      showLoading,
      muteErrorToast,
      requiresLogin: true,
    });
  }

  getResellerPaymentLink(uid: any) {
    return this.request(
      "reseller.get-payment-link",
      { uid },
      {
        showLoading: true,
        requiresLogin: true,
      }
    );
  }

  resellerPaymentGetOrderDetail(id: any) {
    return this.request(
      "reseller.get-order-detail",
      { id },
      {
        showLoading: false,
        requiresLogin: true,
      }
    );
  }

  resellerPaymentGetResellerOrderList() {
    return this.request(
      "reseller.get-reseller-order-list",
      {},
      {
        showLoading: false,
        requiresLogin: true,
      }
    );
  }

  resellerPaymentGetResellerOrderDetail(id: any) {
    return this.request(
      "reseller.get-reseller-order-detail",
      { id },
      {
        showLoading: false,
        requiresLogin: true,
      }
    );
  }

  payResellerPaymentLink(uid: any) {
    return this.request(
      "reseller.pay-payment-link",
      { uid },
      {
        showLoading: true,
        requiresLogin: true,
      }
    );
  }
}

export interface loginResponse {
  token: string;
}

export interface getUserInfoResponse {
  id: number;
  headimgurl: string;
  nickname: string;
}



// WEBPACK FOOTER //
// ./src/services/api.ts